/**
 * Quick add drawer — opens immediately with a skeleton, then fetches a product
 * section. Handles focus containment, background inerting and stale requests.
 */
(() => {
  const DRAWER_TAG = 'quick-add-drawer';
  const GALLERY_TAG = 'quick-add-gallery';
  const OPEN_EVENT = 'quick_add_drawer_open';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class QuickAddGallery extends HTMLElement {
    #pendingMediaId = '';
    #attempts = 0;

    connectedCallback() {
      this.#pendingMediaId = this.dataset.selectedMediaId || '';
      this.#mountAndSelect();
    }

    selectByMediaId(mediaId) {
      if (!mediaId) return;

      this.#pendingMediaId = String(mediaId);
      this.#attempts = 0;
      this.#mountAndSelect();
    }

    #mountAndSelect() {
      window.themeCarousel?.evaluateAll?.(this);

      const carousel = this.querySelector('[data-carousel]');
      const slides = [...this.querySelectorAll('[data-media-id]')];
      const index = slides.findIndex(
        (slide) => slide.getAttribute('data-media-id') === this.#pendingMediaId
      );

      if (index >= 0 && carousel?.__splide) {
        carousel.__splide.go(index);
        this.#attempts = 0;
        return;
      }

      if (this.#attempts >= 10) return;

      this.#attempts += 1;
      window.setTimeout(() => this.#mountAndSelect(), 50);
    }
  }

  class QuickAddDrawer extends HTMLElement {
    #abort = null;
    #defaultHeadingId = '';
    #inerted = [];
    #open = false;
    #opener = null;
    #previousOverflow = '';
    #requestToken = 0;

    connectedCallback() {
      this.#defaultHeadingId =
        this.#panel()?.getAttribute('aria-labelledby') || '';
      document.addEventListener(OPEN_EVENT, this.#onOpen);
      document.addEventListener('keydown', this.#onKeydown);
      this.addEventListener('click', this.#onClick);
    }

    disconnectedCallback() {
      document.removeEventListener(OPEN_EVENT, this.#onOpen);
      document.removeEventListener('keydown', this.#onKeydown);
      this.removeEventListener('click', this.#onClick);
      this.#abort?.abort();
      this.#unlockScroll();
      this.#releaseBackground();
    }

    get isOpen() {
      return this.#open;
    }

    get opener() {
      return this.#opener;
    }

    open({ url, variantId, opener } = {}) {
      if (!url) return;

      const safeUrl = this.#productUrl(url);
      if (!safeUrl) return;

      const quickCart = document.querySelector('quick-cart');
      quickCart?.hide?.({ restoreFocus: false });

      this.#opener =
        opener instanceof HTMLElement
          ? opener
          : document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;

      this.#show();
      this.#load(safeUrl, variantId);
    }

    hide({ restoreFocus = true } = {}) {
      this.#hide(restoreFocus);
    }

    #panel() {
      return this.querySelector('[data-quick-add-drawer-panel]');
    }

    #closeButton() {
      return this.querySelector('[data-quick-add-drawer-close]');
    }

    #body() {
      return this.querySelector('[data-quick-add-drawer-body]');
    }

    #content() {
      return this.querySelector('[data-quick-add-content]');
    }

    #extras() {
      return this.querySelector('[data-quick-add-extras]');
    }

    #status() {
      return this.querySelector('[data-quick-add-drawer-status]');
    }

    #detailsLink() {
      return this.querySelector('[data-quick-add-details-link]');
    }

    #productUrl(value) {
      try {
        const url = new URL(value, window.location.origin);

        if (url.origin !== window.location.origin) return null;

        return url;
      } catch {
        return null;
      }
    }

    #onOpen = (event) => {
      const { url, variantId, opener } = event.detail || {};

      if (!url) return;

      event.preventDefault();
      this.open({ url, variantId, opener: opener || event.target });
    };

    #onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (
        target.closest('[data-quick-add-drawer-close]') ||
        target.closest('[data-quick-add-drawer-backdrop]')
      ) {
        event.preventDefault();
        this.#hide(true);
      }
    };

    #onKeydown = (event) => {
      if (!this.#open) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.#hide(true);
        return;
      }

      if (event.key !== 'Tab') return;

      this.#trapTab(event);
    };

    #trapTab(event) {
      const panel = this.#panel();
      if (!panel) return;

      const focusable = [...panel.querySelectorAll(FOCUSABLE)].filter(
        (element) =>
          element instanceof HTMLElement &&
          element.getClientRects().length > 0
      );

      if (focusable.length === 0) {
        event.preventDefault();
        this.#closeButton()?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    #show() {
      if (!this.#open) {
        this.#open = true;
        this.setAttribute('data-open', 'true');
        this.setAttribute('aria-hidden', 'false');
        this.#lockScroll();
        this.#holdBackground();
      }

      const body = this.#body();
      if (body instanceof HTMLElement) body.scrollTop = 0;

      this.#closeButton()?.focus();
    }

    #hide(restoreFocus) {
      this.#abort?.abort();
      this.#abort = null;
      this.#requestToken += 1;

      if (!this.#open) return;

      this.#open = false;
      this.removeAttribute('data-open');
      this.setAttribute('aria-hidden', 'true');
      this.#unlockScroll();
      this.#releaseBackground();

      const opener = this.#opener;
      this.#opener = null;

      if (restoreFocus && opener?.isConnected) {
        requestAnimationFrame(() => opener.focus());
      }
    }

    async #load(productUrl, variantId) {
      this.#abort?.abort();
      this.#abort = new AbortController();
      const token = ++this.#requestToken;

      this.#showSkeleton();

      const requestUrl = new URL(productUrl);
      requestUrl.searchParams.set(
        'section_id',
        this.dataset.productSectionId || 'quick-add-product'
      );

      if (variantId) {
        requestUrl.searchParams.set('variant', String(variantId));
      }

      try {
        const response = await fetch(requestUrl.toString(), {
          signal: this.#abort.signal,
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok || token !== this.#requestToken) {
          throw new Error('Quick add request failed');
        }

        const html = await response.text();
        if (token !== this.#requestToken) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const product = doc.querySelector('[data-quick-add-product]');

        if (!product) throw new Error('Quick add content missing');

        const content = this.#content();
        if (!content) return;

        const fragment = document.createDocumentFragment();

        for (const style of doc.querySelectorAll('style')) {
          fragment.append(style.cloneNode(true));
        }

        fragment.append(product);
        content.replaceChildren(fragment);

        const title = product.getAttribute('data-product-title') || '';
        const productPath = product.getAttribute('data-product-url') || productUrl.pathname;
        const titleId = product.getAttribute('data-title-id');
        const detailsLink = this.#detailsLink();
        const extras = this.#extras();

        if (detailsLink instanceof HTMLAnchorElement) {
          detailsLink.href = productPath;
        }

        if (extras instanceof HTMLElement) extras.hidden = false;
        if (titleId) this.#panel()?.setAttribute('aria-labelledby', titleId);

        window.themeCarousel?.evaluateAll?.(content);

        this.#announce(
          (this.dataset.loadedLabel || '').replace('__title__', title)
        );
      } catch (error) {
        if (error?.name === 'AbortError' || token !== this.#requestToken) return;

        this.#showError(productUrl);
      }
    }

    #showSkeleton() {
      const content = this.#content();
      const extras = this.#extras();

      if (!content) return;

      content.innerHTML = `
        <div class="quick-add-drawer__skeleton" aria-hidden="true">
          <span class="quick-add-drawer__skeleton-media"></span>
          <span class="quick-add-drawer__skeleton-line quick-add-drawer__skeleton-line--badges"></span>
          <span class="quick-add-drawer__skeleton-line quick-add-drawer__skeleton-line--title"></span>
          <span class="quick-add-drawer__skeleton-line quick-add-drawer__skeleton-line--short"></span>
          <span class="quick-add-drawer__skeleton-line quick-add-drawer__skeleton-line--options"></span>
          <span class="quick-add-drawer__skeleton-line quick-add-drawer__skeleton-line--button"></span>
        </div>
      `;

      if (extras instanceof HTMLElement) extras.hidden = true;
      this.#panel()?.setAttribute('aria-labelledby', this.#defaultHeadingId);
      this.#announce(this.dataset.loadingLabel || '');
    }

    #showError(productUrl) {
      const content = this.#content();
      const extras = this.#extras();
      if (!content) return;

      const root = document.createElement('div');
      const message = document.createElement('p');
      const link = document.createElement('a');

      root.className = 'quick-add-drawer__error';
      message.className = 'quick-add-drawer__error-message';
      message.textContent = this.dataset.errorLabel || '';
      link.className = 'quick-add-drawer__details-link';
      link.href = productUrl.toString();
      link.textContent = this.#detailsLink()?.textContent?.trim() || '';

      root.append(message, link);
      content.replaceChildren(root);

      if (extras instanceof HTMLElement) extras.hidden = true;
      this.#announce(this.dataset.errorLabel || '');
      link.focus();
    }

    #announce(message) {
      const status = this.#status();
      if (!(status instanceof HTMLElement)) return;

      status.textContent = '';
      requestAnimationFrame(() => {
        status.textContent = message;
      });
    }

    #holdBackground() {
      for (const node of document.body.children) {
        if (node === this || node.contains(this)) continue;
        if (node.hasAttribute('inert')) continue;

        node.setAttribute('inert', '');
        this.#inerted.push(node);
      }
    }

    #releaseBackground() {
      for (const node of this.#inerted) {
        node.removeAttribute('inert');
      }

      this.#inerted = [];
    }

    #lockScroll() {
      this.#previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }

    #unlockScroll() {
      document.documentElement.style.overflow = this.#previousOverflow;
      this.#previousOverflow = '';
    }
  }

  if (!customElements.get(GALLERY_TAG)) {
    customElements.define(GALLERY_TAG, QuickAddGallery);
  }

  if (!customElements.get(DRAWER_TAG)) {
    customElements.define(DRAWER_TAG, QuickAddDrawer);
  }
})();
